/**
 * Sprint F (Feb 2026) — Mitarbeiter-Nachschlagewerk Suchleiste mit Live-Treffern.
 *
 * Designkonsequenz:
 * - KEIN eigenes Menü. KEIN eigener Tab. Suchleiste liegt direkt im Monteur-
 *   Aufgaben-Screen und ist immer sichtbar.
 * - Live-Suche per Debounce (160 ms) — Reaktionszeit < 200 ms gemäß Sprint-F-Vorgabe.
 * - Treffer erscheinen direkt unter der Suchleiste, mit Snippet-Vorschau.
 * - Highlighten der Suchphrase im Snippet (visueller Marker, kein dangerouslySetInnerHTML).
 *
 * CLARTEXT SPRACHE V1 – Phase 2: UI-Rahmentexte über t(). Treffer-Inhalte
 * (Welt-Titel, Snippet, Feld-Label vom Backend) sind Daten und bleiben deutsch.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useMonteurT } from "@/i18n/monteur";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Search, Loader2, BookOpen } from "lucide-react";

const DEBOUNCE_MS = 160;

function HighlightedSnippet({ text, query }) {
  if (!query || !text) return <>{text}</>;
  // Case-insensitive Treffer hervorheben, ohne dangerouslySetInnerHTML.
  const lower = text.toLowerCase();
  const q = query.toLowerCase();
  const parts = [];
  let cursor = 0;
  while (cursor < text.length) {
    const idx = lower.indexOf(q, cursor);
    if (idx < 0) {
      parts.push(<span key={cursor}>{text.slice(cursor)}</span>);
      break;
    }
    if (idx > cursor) {
      parts.push(<span key={`p${cursor}`}>{text.slice(cursor, idx)}</span>);
    }
    parts.push(
      <mark
        key={`m${idx}`}
        className="bg-amber-200 text-slate-900 rounded px-0.5"
      >
        {text.slice(idx, idx + q.length)}
      </mark>
    );
    cursor = idx + q.length;
  }
  return <>{parts}</>;
}

export default function MonteurWeltSuche({ headingWhenEmpty }) {
  const navigate = useNavigate();
  const t = useMonteurT();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  const onQueryChange = (value) => {
    setQuery(value);
    // Wenn weniger als 2 Zeichen, gibt es keine Suche — UI sofort entspannen.
    if (value.trim().length < 2) {
      setResults([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = query.trim();
    if (trimmed.length < 2) return undefined;
    const reqId = ++requestIdRef.current;
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      setTouched(true);
      api
        .searchMyWorlds(trimmed)
        .then((data) => {
          // Spät-eingetroffene Antworten verwerfen (race-safe).
          if (reqId !== requestIdRef.current) return;
          setResults(Array.isArray(data) ? data : []);
        })
        .catch(() => {
          if (reqId !== requestIdRef.current) return;
          setResults([]);
        })
        .finally(() => {
          if (reqId !== requestIdRef.current) return;
          setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const trimmed = query.trim();
  const showResults = trimmed.length >= 2;
  const noResults = showResults && !loading && results.length === 0 && touched;

  return (
    <section data-testid="monteur-welt-suche">
      {headingWhenEmpty && (
        <h3 className="font-heading text-base font-semibold text-slate-900 mb-2">
          {headingWhenEmpty}
        </h3>
      )}
      <div className="relative">
        <Search className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
        <Input
          type="search"
          inputMode="search"
          autoComplete="off"
          placeholder={t("suche.placeholder")}
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          className="pl-12 pr-12 h-12 text-base bg-white border-slate-300 focus-visible:ring-slate-400"
          data-testid="monteur-welt-suche-input"
          aria-label={t("suche.inputAria")}
        />
        {loading && (
          <Loader2
            className="w-4 h-4 text-slate-400 animate-spin absolute right-4 top-1/2 -translate-y-1/2"
            data-testid="monteur-welt-suche-loading"
          />
        )}
      </div>

      {showResults && (
        <div
          className="mt-3 space-y-2"
          data-testid="monteur-welt-suche-results"
        >
          {results.map((hit) => (
            <button
              key={hit.id}
              onClick={() => navigate(`/monteur/welt/${hit.id}`)}
              className="w-full text-left"
              data-testid={`monteur-welt-suche-result-${hit.id}`}
            >
              <Card className="bg-white border border-slate-200 hover:border-slate-400 hover:shadow-sm transition-all p-4">
                <div className="flex items-start gap-3">
                  <BookOpen className="w-5 h-5 text-slate-500 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="font-heading font-semibold text-slate-900 text-base leading-snug truncate">
                      {hit.title}
                    </div>
                    {hit.match_field !== "title" && hit.match_snippet && (
                      <div className="text-xs text-slate-500 mt-1.5">
                        <span className="uppercase tracking-wider text-[10px] text-slate-400 mr-1.5">
                          {t("suche.matchIn", { label: hit.match_field_label })}
                        </span>
                        <span className="text-slate-700">
                          <HighlightedSnippet
                            text={hit.match_snippet}
                            query={trimmed}
                          />
                        </span>
                      </div>
                    )}
                    {hit.match_field === "title" && hit.short_description && (
                      <div className="text-xs text-slate-500 mt-1 truncate">
                        {hit.short_description}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </button>
          ))}
          {noResults && (
            <Card
              className="bg-slate-50 border border-slate-200 p-4 text-center"
              data-testid="monteur-welt-suche-empty"
            >
              <p className="text-sm text-slate-600">
                {t("suche.empty")}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {t("suche.emptyHint")}
              </p>
            </Card>
          )}
        </div>
      )}
    </section>
  );
}
